import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Utils } from '../../utils';
import { Desktop } from './Desktop';
import { Mobile } from './Mobile';

import { ERROR_MESSAGES } from '../../constants';
import { useContextSelector } from '../../hooks';
import type {
  CarSearchData,
  CarSearchFilters,
  CarSearchInitialData,
  GetSearchLocation,
  ISearchState,
  ItineraryData,
  RentalType,
  SearchInputType,
} from '../../types';

declare const window: Window & { dataLayer: Record<string, unknown>[]; };
export interface ICarSearchProps {
  className?: string;
  initialData?: CarSearchInitialData;
  disabledOverlay?: boolean;
  triggerErrorClear?: boolean;
  filterSearchBy?: CarSearchFilters;
  onOverlayClick?: () => void;
  onInitialDataResolve?: (searchData: ISearchState) => void;
  onSearch?: (data: CarSearchData, url: string) => void;
}

export const CarSearch = ({
  className = '',
  initialData,
  filterSearchBy = 'all',
  disabledOverlay = true,
  triggerErrorClear = false,
  onOverlayClick = () => undefined,
  onInitialDataResolve = () => undefined,
  onSearch = undefined,
  ...props
}: ICarSearchProps) => {
  const history = useHistory();
  const {
    errors: { datesError, locationError, timeError },
    generics: { isMobile },
    search: { dates, differentAgencies, location, time },
    update,
  } = useContextSelector();

  // should only treat initial data and call getPlaces service once.
  const [didFirstInitialDataCheck, setDidFirstInitialDataCheck] = useState<boolean>(false);

  useEffect(() => {
    if (triggerErrorClear) {
      update.errors.reset();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerErrorClear]);

  useEffect(() => {
    const handleLocationFetch = async (data: CarSearchInitialData, filter: CarSearchFilters) => {
      const returnPayload: ISearchState = {
        differentAgencies: data.differentAgencies,
        dates: { dropoff: data.dates.dropoff, pickup: data.dates.pickup },
        location: { dropoff: null, pickup: null },
        time: { dropoff: data.time.dropoff, pickup: data.time.pickup },
      };

      const pickupResponse = await Utils.initialData.getLocationResponse(data.location.pickup, filter);

      if (pickupResponse) {
        update.search.location.pickup(pickupResponse);
        returnPayload.location.pickup = pickupResponse;
      }

      if (data.location.pickup !== data.location.dropoff) {
        const dropoffResponse = await Utils.initialData.getLocationResponse(data.location.dropoff, filter);

        if (dropoffResponse) {
          update.search.location.dropoff(dropoffResponse);
          returnPayload.location.dropoff = dropoffResponse;
        }
      } else {
        update.search.location.dropoff(pickupResponse);
        returnPayload.location.pickup = pickupResponse;
      }

      update.search.dates.pickup(data.dates.pickup);
      update.search.dates.dropoff(data.dates.dropoff);
      update.search.time.pickup(data.time.pickup);
      update.search.time.dropoff(data.time.dropoff);
      update.search.differentAgencies(data.differentAgencies);

      onInitialDataResolve(returnPayload);
    };

    if (initialData && !didFirstInitialDataCheck) {
      handleLocationFetch(initialData, filterSearchBy);
      setDidFirstInitialDataCheck(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

    useEffect(() => {
      window.dataLayer = window.dataLayer || [];
  }, []);

  const pushEventToDataLayer = (terms: string, dateBetween: string) => {
    window.dataLayer.push({
      event: 'search',
      prod_type: 'CARROS',
      search_terms: terms,
      passagers: "",
      period: dateBetween
    });
  }

  const formatDate = (dateStr: string) : string => {
    const dateToFormat = new Date(dateStr)
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    };

    const formattedDate = new Intl.DateTimeFormat('pt-BR', options).format(dateToFormat);
    return formattedDate.replace(/\//g, '-');
  };

  const handleDesktopSearchFocus = () => {
    if (!isMobile) {
      const overlay = document.querySelector<HTMLDivElement>('.car-search-overlay');

      if (overlay && !disabledOverlay) {
        overlay.classList.add('active');
        document.body.style.overflow = 'hidden';
      }
    }
  };

  const clearOverlay = () => {
    const overlay = document.querySelector<HTMLDivElement>('.car-search-overlay');

    if (overlay) {
      overlay.classList.remove('active');
      document.body.removeAttribute('style');
      onOverlayClick();
    }
  };

  const handleDifferentAgenciesCheck = (id: string) => {
    const input = document.querySelector(`#${id}`) as HTMLInputElement;
    update.search.differentAgencies(input.checked);

    if (input.checked) {
      update.search.location.dropoff(null);
    } else if (location.pickup) {
      update.search.location.dropoff(location.pickup);
    }

    update.errors.reset();
  };

  const handleLocationSelect = (selectedLocation: GetSearchLocation, type: SearchInputType) => {
    if (type === 'pickup') {
      update.search.location.pickup(selectedLocation);
      update.search.dates.pickup(null);
      update.search.time.pickup(null);
      update.search.location.dropoff(null);
      update.search.dates.dropoff(null);
      update.search.time.dropoff(null);
      update.inputs.openLocationDropoff();
    } else if (type === 'dropoff') {
      update.search.location.dropoff(selectedLocation);

      if (!dates.dropoff) {
        update.inputs.openDates();
      }
    } else {
      update.search.location.pickup(selectedLocation);
      update.search.dates.pickup(null);
      update.search.time.pickup(null);
      update.search.location.dropoff(selectedLocation);
      update.search.dates.dropoff(null);
      update.search.time.dropoff(null);
      update.inputs.openDates();
    }

    if (locationError[type].active) {
      update.errors.locationError[type](false);
    }
  };

  const handleDateSelect = (selectedDates: ItineraryData) => {
    const todayDate = new Date();
    const isInternational = location.pickup?.region === 'international';

    if (selectedDates.pickup) {
      update.search.dates.pickup(selectedDates.pickup);
      update.search.dates.dropoff(selectedDates.dropoff);

      if (dates.dropoff) {
        update.search.time.dropoff(null);
      }
    }

    if (selectedDates.dropoff) {
      const selectedPickupDate = new Date(selectedDates.pickup as string);
      const selectedDropoff = new Date(selectedDates.dropoff);

      if (isInternational && selectedPickupDate > todayDate) {
        update.search.time.pickup('10:00');
        update.search.time.dropoff('10:00');
      } else {
        update.search.time.pickup(null);
        update.search.time.dropoff(null);
      }

      if (selectedDropoff <= selectedPickupDate) {
        update.search.dates.dropoff(null);
        update.errors.datesError.dropoff(true, ERROR_MESSAGES.dates.dropoff.sameDate);
      } else {
        update.search.dates.dropoff(selectedDates.dropoff);

        if (isMobile) {
          update.inputs.openTimeModal();
        } else {
          update.inputs.openTimePickup();
        }
      }
    }

    if (datesError.pickup.active) {
      update.errors.datesError.pickup(false);
    }

    if (datesError.dropoff.active) {
      update.errors.datesError.dropoff(false);
    }
  };

  const handleTimeSelect = (selectedTime: string, type: RentalType) => {
    if (type === 'pickup') {
      update.search.time.pickup(selectedTime);

      if (!time.dropoff) {
        update.search.time.dropoff(selectedTime);
      }
    }

    if (type === 'dropoff') {
      update.search.time.dropoff(selectedTime);
    }

    update.inputs.closeAllInputs();

    if (timeError[type].active) {
      update.errors.timeError[type](false);
    }
  };

  const handleTimeSelectMobile = (selectedTime: ItineraryData) => {
    if (selectedTime.pickup) {
      update.search.time.pickup(selectedTime.pickup);
    }

    if (selectedTime.dropoff) {
      update.search.time.dropoff(selectedTime.dropoff);
    }

    update.inputs.closeAllInputs();

    if (timeError.pickup.active) {
      update.errors.datesError.pickup(false);
    }

    if (timeError.dropoff.active) {
      update.errors.datesError.dropoff(false);
    }
  };

  const handleSubmit = () => {
    update.errors.reset();

    const data: [string, any][] = [
      ['location', location],
      ['dates', dates],
      ['time', time],
    ];

    const hasEmptyFields = Utils.error.validateInputs(data, differentAgencies, update.errors);

    if (hasEmptyFields) {
      return;
    }

    const output = { location, dates, time } as CarSearchData;

    const dataLayerParams = {
      searchTerms: data[0][1].pickup.name,
      period: `${formatDate(data[1][1].pickup)} | ${formatDate(data[1][1].dropoff)}`
    }
    pushEventToDataLayer(dataLayerParams.searchTerms, dataLayerParams.period)


    switch (location.pickup?.region) {
      case 'national':
        // eslint-disable-next-line no-case-declarations
        const nationalURLQueries = Utils.submit.buildNationalURL(location, dates, time);

        if (onSearch) {
          onSearch(output, nationalURLQueries);
        } else {
          window.open(`/mfe/carros/nacional/resultados?${nationalURLQueries}`);
        }
        break;
      case 'international':
        // eslint-disable-next-line no-case-declarations
        const internationalURLQueries = Utils.submit.buildInternationalURL(location, dates, time);

        if (onSearch) {
          onSearch(output, internationalURLQueries);
        } else {
          window.open(`/mfe/carros/internacional/resultados?${internationalURLQueries}`);
        }
        break;
      default:
        break;
    }
  };

  return (
    <>
      <section
        id="smiles-car-search-panel"
        className={`car-search-container ${className}`.trim()}
        onClick={handleDesktopSearchFocus}
        {...props}
      >
        {isMobile ? (
          <Mobile
            filterSearchBy={filterSearchBy}
            onLocationSelect={handleLocationSelect}
            onDatesSelect={handleDateSelect}
            onTimeSelect={handleTimeSelectMobile}
            onDifferentAgencyCheck={handleDifferentAgenciesCheck}
            onSubmit={handleSubmit}
          />
        ) : (
          <Desktop
            filterSearchBy={filterSearchBy}
            onLocationSelect={handleLocationSelect}
            onDatesSelect={handleDateSelect}
            onTimeSelect={handleTimeSelect}
            onDifferentAgencyCheck={handleDifferentAgenciesCheck}
            onSubmit={handleSubmit}
          />
        )}
      </section>

      {!isMobile && !disabledOverlay && <div className="car-search-overlay" onClick={clearOverlay} />}
    </>
  );
};
